import { OpenAPIV3 } from 'openapi-types';
import * as OpenAPISampler from 'openapi-sampler';
import { escape, merge } from 'lodash';
import { JSONSchema7 } from 'json-schema';
import * as inflection from 'inflection';

const samplePrimitive = (schema: OpenAPIV3.SchemaObject): string => {
  const PrimitiveTypes = ['integer', 'number', 'string', 'boolean', undefined /* occasionally type is missing */];
  if (!PrimitiveTypes.includes(schema.type)) {
    throw new Error(`Not a primitive: ${schema.type}`);
  }
  return `${OpenAPISampler.sample(schema as JSONSchema7)}`;
};

const renderXmlElement = (
  tagName: string,
  attributes: Array<[string, string]>,
  children: string[],
  opts?: { singleLine: boolean }
) => {
  let renderedAttributes = attributes.map(([name, value]) => `${name}="${escape(value)}"`).join(' ');
  if (renderedAttributes.length) {
    renderedAttributes = ` ${renderedAttributes}`;
  }

  const hasChildren = children.length;
  const body = opts?.singleLine
    ? children.join('')
    : `\n${children
        .flatMap(line => line.split('\n'))
        .map(line => `  ${line}`)
        .join('\n')}${hasChildren ? '\n' : ''}`;

  return `<${tagName}${renderedAttributes}>${body}</${tagName}>`;
};

const resolveProperties = (
  schema: OpenAPIV3.SchemaObject,
  skipNonRequired: boolean
): {
  attributes: Array<[string, string]>;
  propertiesAsChildren: string[];
  bodyText: string | null;
} => {
  const properties = Object.entries(schema.properties ?? {}) as Array<[string, OpenAPIV3.SchemaObject]>;
  const requiredProperties = schema.required ?? [];
  const includedProperties = properties.filter(([name]) =>
    skipNonRequired ? requiredProperties.includes(name) : true
  );

  // These properties should be rendered as attributes, e.g. <obj property="value"></obj>
  const attributes = includedProperties
    .filter(([, value]) => value.xml?.attribute)
    .map(([name, value]) => [value.xml?.name ?? name, samplePrimitive(value)] as [string, string]);

  // Otherwise they should be rendered as children, e.g. <obj><property>value</property></obj>
  const childProperties = includedProperties
    .filter(([, value]) => !value.xml?.attribute)
    // eslint-disable-next-line no-use-before-define
    .map(([key, value]) => renderXmlFromSchema(value as OpenAPIV3.SchemaObject, key, skipNonRequired));

  // Support special x-text attribute to set the body text: see https://github.com/OAI/OpenAPI-Specification/issues/630
  // e.g. <obj>value</obj>
  const xTextProperties = includedProperties.filter(
    ([, value]) => value.xml && (value.xml as Record<string, string>)['x-text']
  );
  if (xTextProperties.length > 1) {
    throw new Error('Only one property with x-text directive is allowed.');
  }

  return {
    attributes,
    propertiesAsChildren: childProperties,
    bodyText: xTextProperties.length ? samplePrimitive(xTextProperties[0][1]) : null,
  };
};

const renderXmlFromSchema = (
  schema: OpenAPIV3.SchemaObject,
  schemaName: string,
  skipNonRequired: boolean,
  sampleValue?: string
): string => {
  // Handle combined schemas
  if (schema.oneOf) {
    // Always choose the first
    return renderXmlFromSchema(schema.oneOf[0] as OpenAPIV3.SchemaObject, schemaName, skipNonRequired);
  }
  if (schema.allOf) {
    return renderXmlFromSchema(merge({}, ...schema.allOf) as OpenAPIV3.SchemaObject, schemaName, skipNonRequired);
  }
  if (schema.anyOf) {
    throw new Error('"anyOf" is unsupported');
  }
  if (schema.not) {
    throw new Error('"not" is unsupported');
  }

  const tagName = schema.xml?.name ?? schemaName;
  switch (schema.type) {
    case undefined: // Default to object
    case 'object': {
      const properties = resolveProperties(schema, skipNonRequired);

      const children = properties.bodyText ? [escape(properties.bodyText)] : properties.propertiesAsChildren;

      return renderXmlElement(tagName, properties.attributes, children, { singleLine: !!properties.bodyText });
    }
    case 'array': {
      const itemType = schema.items as OpenAPIV3.SchemaObject;

      // NON-STANDARD: oas_parser uses the singular of the tagName for the items instead of tagName
      const itemSchemaName = inflection.singularize(tagName);

      let children: string[] = schema.example
        ? schema.example.map((value: unknown) =>
            renderXmlFromSchema(itemType, itemSchemaName, skipNonRequired, `${value}`)
          )
        : [renderXmlFromSchema(itemType, itemSchemaName, skipNonRequired)];
      if (skipNonRequired) {
        children = children.slice(0, schema.minItems ?? 0);
      }

      // NON-STANDARD: oas_parser supports attributes on the array, e.g. <array count="1">...
      const { attributes } = resolveProperties(schema, skipNonRequired);

      // NON-STANDARD: oas_parser ALWAYS wraps the array in an element instead of only when xml.wrapped is set
      return renderXmlElement(tagName, attributes, children);
    }
    case 'integer':
    case 'number':
    case 'string':
    case 'boolean': {
      return renderXmlElement(tagName, [], [escape(sampleValue ?? samplePrimitive(schema))], {
        singleLine: true,
      });
    }
    default:
      throw new Error(`Unknown schema type: ${(schema as OpenAPIV3.SchemaObject).type}`);
  }
};

/**
 * Creates a sample XML file from an OpenAPI schema. Attempts to generate the same XML as the Nexmo OAS Parser
 * (https://github.com/Nexmo/oas_parser), which is not fully compliant with the OpenAPI spec:
 * https://swagger.io/docs/specification/data-models/representing-xml/
 */
// eslint-disable-next-line import/prefer-default-export
export const sampleNexmoXmlSchema = (
  schema: OpenAPIV3.SchemaObject,
  skipNonRequired: boolean
): string => `<?xml version="1.0" encoding="UTF-8"?>
${renderXmlFromSchema(schema, 'notagname', skipNonRequired)}`;
